import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { intl } from "LocaleWrapper";
import { useLazyQuery, useMutation } from "@apollo/client";

import { zodResolver } from "@hookform/resolvers/zod";

import { Input, Card, FormGroup, PageHeader, Button } from "@cofano/cck";
import { Grid2 as Grid } from "@mui/material";

import { FormErrorMessage } from "components/form/FormErrorMessage";
import Language from "components/utils/Language";
import Helmet from "components/helmet/Helmet";
import NumberInput from "components/cck/NumberInput";

import VatCodeTypeSelect from "./components/selects/VatCodeTypeSelect";
import VatCodeTransactionTypeSelect from "./components/selects/VatCodeTransactionTypeSelect";
import VatCodeAllocationSelect from "./components/selects/VatCodeAllocationSelect";

import { vatCodeSchema, VatCodeSchemaInput, VatCodeSchemaOutput } from "./utils/vatCode.schema";

import { TransactionType, VatCodeType, VatRelation } from "__generated__/enums";
import { SaveVatCodeDocument } from "__generated__/view/vatcodes/graphql/vatCode.mutations.generated";
import { GetVatCodeDocument } from "__generated__/view/vatcodes/graphql/vatCode.queries.generated";

const VatCodeCreateEdit = () => {

	const navigate = useNavigate();
	const { id } = useParams();

	const [ saveVatCode, { loading } ] = useMutation(SaveVatCodeDocument);

	const [ getVatCode ]  = useLazyQuery(GetVatCodeDocument);

	const { control, handleSubmit, reset } = useForm<VatCodeSchemaInput, unknown, VatCodeSchemaOutput>({
		resolver: zodResolver(vatCodeSchema),
		defaultValues: {
			id: null,
			code: null,
			description: "",
			percentage: 0,
			type: VatCodeType.Inclusive,
			transactionType: TransactionType.Purchase,
			allocation: VatRelation.BothInNation,
		},
	});

	const save = (data: VatCodeSchemaOutput) => {
		saveVatCode({
			variables: {
				input: {
					id: data.id,
					code: data.code,
					description: data.description,
					percentage: data.percentage,
					type: data.type,
					transactionType: data.transactionType,
					allocation: data.allocation,
				},
			},
		}).then(() => navigate("/view/vatcodes"));
	};

	useEffect(() => {
		if (id) {
			getVatCode({
				variables: {
					id: Number(id),
				},
			}).then(({ data }) => {
				const vatCode = data?.vatCode;

				if (vatCode) {
					reset(vatCode);
				}
			});
		}
	}, [ id ]);

	return (
		<form onSubmit={handleSubmit(save)}>
			<PageHeader>
				<PageHeader.Title>
					{Language("VAT_CODE")}
					<Helmet title={intl.formatMessage({ id: "VAT_CODE" })} />
				</PageHeader.Title>

				<PageHeader.Actions>
					<Button onClick={() => navigate("/view/vatcodes")} variant="outlined">
						{Language("CANCEL")}
					</Button>

					<Button loading={loading} type="submit">
						{Language("SAVE")}
					</Button>
				</PageHeader.Actions>
			</PageHeader>

			<Grid spacing={3} container>
				<Grid size={{ xs: 12, lg: 6 }}>
					<Card>
						<FormGroup
							label={Language("CODE")}
							labelPlacement="left-center"
							overflow="hidden"
							required>
							<Controller
								name="code"
								control={control}
								render={({ field, formState }) => (
									<React.Fragment>
										<NumberInput {...field} decimalScale={0} />
										<FormErrorMessage name={field.name} errors={formState.errors} />
									</React.Fragment>
								)} />
						</FormGroup>

						<FormGroup
							label={Language("DESCRIPTION")}
							labelPlacement="left-center"
							overflow="hidden"
							required>
							<Controller
								name="description"
								control={control}
								render={({ field, formState }) => (
									<React.Fragment>
										<Input {...field} />
										<FormErrorMessage name={field.name} errors={formState.errors} />
									</React.Fragment>
								)} />
						</FormGroup>

						<FormGroup
							label={Language("PERCENTAGE")}
							labelPlacement="left-center"
							overflow="hidden"
							required>
							<Controller
								name="percentage"
								control={control}
								render={({ field, formState }) => (
									<React.Fragment>
										<NumberInput {...field} addonAfter={"%"} />
										<FormErrorMessage name={field.name} errors={formState.errors} />
									</React.Fragment>
								)} />
						</FormGroup>

						<FormGroup label={Language("TYPE")} labelPlacement="left-center" overflow="hidden">
							<Controller
								name="type"
								control={control}
								render={({ field }) => <VatCodeTypeSelect {...field} clearable={false} />} />
						</FormGroup>

						<FormGroup label={Language("TRANSACTION_TYPE")} labelPlacement="left-center" overflow="hidden">
							<Controller
								name="transactionType"
								control={control}
								render={({ field }) => <VatCodeTransactionTypeSelect {...field} clearable={false} />} />
						</FormGroup>

						<FormGroup label={Language("ALLOCATION")} labelPlacement="left-center" overflow="hidden">
							<Controller
								name="allocation"
								control={control}
								render={({ field }) => <VatCodeAllocationSelect {...field} clearable={false} />} />
						</FormGroup>
					</Card>
				</Grid>
			</Grid>
		</form>
	);
};

export default VatCodeCreateEdit;
